import type {
  AssetCatalogItemDto,
  AvailableAssetCatalogLocationDto,
  TrayCatalogDetailsDto,
} from '@peritasai/types';
import { AssetType, TrayType } from '@peritasai/types';

export class AssetCatalogItemEntity {
  public readonly id: string;
  public readonly type: AssetType;
  public readonly name: string;
  public readonly description: string | null;
  public readonly manufacturer: string;
  public readonly productNumber: string;
  public readonly metadata: Record<string, unknown> | null;
  public readonly isActive: boolean;
  public readonly availableCount: number;
  public readonly availableAssetLocations: AvailableAssetCatalogLocationDto[];
  public readonly trayDetails: TrayCatalogDetailsDto | null;

  constructor(dto: AssetCatalogItemDto) {
    this.id = dto.id;
    this.type = dto.type;
    this.name = dto.name;
    this.description = dto.description;
    this.manufacturer = dto.manufacturer;
    this.productNumber = dto.productNumber;
    this.metadata = dto.metadata ?? null;
    this.isActive = dto.isActive;
    this.availableCount = dto.availableCount;
    this.availableAssetLocations = dto.availableAssetLocations ?? [];
    this.trayDetails = dto.trayDetails ?? null;
  }

  /**
   * The single best location to point a picker to: the highest-stocked
   * *resolved* rack (locationId !== null). Bulk stock not yet tracked to any
   * specific rack (locationId === null) is excluded unless it's all there
   * is — otherwise its count, often the largest, would outrank a real rack
   * and send the picker nowhere. Undefined when nothing is available anywhere.
   */
  public get bestAvailableLocation(): AvailableAssetCatalogLocationDto | undefined {
    const resolved = this.availableAssetLocations.filter(
      (location) => location.locationId !== null
    );
    const candidates =
      resolved.length > 0 ? resolved : this.availableAssetLocations;
    return [...candidates].sort((a, b) => b.count - a.count)[0];
  }

  /**
   * Tray type inherited by physical trays created from this catalog.
   * Null for non-tray catalogs.
   */
  public get trayType(): TrayType | null {
    return this.trayDetails?.trayType ?? null;
  }
}
